import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { setInterceptorContext, attachContextInterceptor } from "./apiInterceptor";
import { isAnyDirty, discardAllDirty } from "./dirtyRegistry";

/**
 * SuiteContextProvider - Foundation Context Engine (React Provider).
 *
 * Espone stato + azioni per il cambio contesto. Le fetch a
 *   /api/context/resume
 *   /api/context/available
 *   /api/context/history
 *   /api/context/impersonable-users
 * sono INDIPENDENTI: se una fallisce, le altre continuano. Ogni chiamata
 * ha stato di caricamento ed errore proprio, cosi' la UI puo' mostrare
 * messaggi chiari e retry.
 */
const SuiteCtx = createContext(null);
const CACHE_KEY = "gi_suite_context_v1";
const CACHE_HISTORY_KEY = "gi_suite_context_history_v1";

let _interceptorAttached = false;
if (!_interceptorAttached) {
  attachContextInterceptor(api);
  _interceptorAttached = true;
}

function _readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function _writeCache(key, value) {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* noop */ }
}

export function SuiteContextProvider({ children }) {
  const { user } = useAuth();
  const [context, setContext] = useState(() => _readCache(CACHE_KEY));
  const [contextError, setContextError] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);

  const [available, setAvailable] = useState(null);
  const [availableError, setAvailableError] = useState(null);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  const [history, setHistory] = useState(() => _readCache(CACHE_HISTORY_KEY) || []);
  const [impersonables, setImpersonables] = useState([]);
  const [loadingImpersonables, setLoadingImpersonables] = useState(false);

  // NB: rimosso il pattern `mounted.current` (useRef) perche' in React 18
  // StrictMode il double-mount lasciava il ref a `false`, causando il
  // loader infinito (finally non chiamava setLoadingAvailable(false)).
  // Usiamo AbortController per cancellare le fetch in-flight ed evitiamo
  // qualsiasi controllo booleano di mount manuale.

  // --- Fetchers indipendenti (best-effort, non si abbattono a vicenda) ---
  const fetchContext = useCallback(async (signal) => {
    setLoadingContext(true);
    setContextError(null);
    try {
      const { data } = await api.get("/context/resume", { signal });
      setContext(data);
      _writeCache(CACHE_KEY, data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      setContextError(e);
    } finally {
      setLoadingContext(false);
    }
  }, []);

  const fetchAvailable = useCallback(async (signal) => {
    setLoadingAvailable(true);
    setAvailableError(null);
    try {
      const { data } = await api.get("/context/available", { signal });
      setAvailable(data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      setAvailableError(e);
    } finally {
      setLoadingAvailable(false);
    }
  }, []);

  const fetchHistory = useCallback(async (signal) => {
    try {
      const { data } = await api.get("/context/history", { signal });
      setHistory(data);
      _writeCache(CACHE_HISTORY_KEY, data);
    } catch { /* noop */ }
  }, []);

  const fetchImpersonables = useCallback(async (signal) => {
    setLoadingImpersonables(true);
    try {
      const { data } = await api.get("/context/impersonable-users", { signal });
      setImpersonables(Array.isArray(data) ? data : []);
    } catch {
      setImpersonables([]);
    } finally {
      setLoadingImpersonables(false);
    }
  }, []);

  // --- Bootstrap al login/logout ---
  useEffect(() => {
    if (!user) {
      setContext(null); setAvailable(null); setHistory([]); setImpersonables([]);
      setContextError(null); setAvailableError(null);
      setLoadingContext(false); setLoadingAvailable(false); setLoadingImpersonables(false);
      _writeCache(CACHE_KEY, null);
      _writeCache(CACHE_HISTORY_KEY, null);
      return;
    }
    const ac = new AbortController();
    fetchContext(ac.signal);
    fetchAvailable(ac.signal);
    fetchHistory(ac.signal);
    fetchImpersonables(ac.signal);
    return () => ac.abort();
  }, [user, fetchContext, fetchAvailable, fetchHistory, fetchImpersonables]);

  // --- Azioni ---
  const refresh = useCallback(async () => {
    await Promise.all([fetchContext(), fetchAvailable()]);
    return context;
  }, [fetchContext, fetchAvailable, context]);

  const switchContext = useCallback(async (payload, opts = {}) => {
    if (!opts.discard && isAnyDirty()) {
      const err = new Error("UNSAVED_CHANGES");
      err.code = "UNSAVED_CHANGES";
      throw err;
    }
    if (opts.discard) discardAllDirty();
    const { data } = await api.post("/context/switch", payload);
    setContext(data);
    _writeCache(CACHE_KEY, data);
    fetchHistory();
    return data;
  }, [fetchHistory]);

  const startImpersonation = useCallback(async (target_user_id, reason, opts = {}) => {
    if (!opts.discard && isAnyDirty()) {
      const err = new Error("UNSAVED_CHANGES");
      err.code = "UNSAVED_CHANGES";
      throw err;
    }
    if (opts.discard) discardAllDirty();
    const { data } = await api.post("/context/impersonate", { target_user_id, reason });
    setContext(data);
    _writeCache(CACHE_KEY, data);
    fetchHistory();
    return data;
  }, [fetchHistory]);

  const endImpersonation = useCallback(async (reason) => {
    const { data } = await api.post("/context/impersonate/end", { reason });
    setContext(data);
    _writeCache(CACHE_KEY, data);
    fetchHistory();
    return data;
  }, [fetchHistory]);

  const clearHistory = useCallback(async () => {
    await api.delete("/context/history");
    setHistory([]);
    _writeCache(CACHE_HISTORY_KEY, []);
  }, []);

  const value = useMemo(() => ({
    context,
    contextError,
    loading: loadingContext,
    loadingContext,
    available,
    availableError,
    loadingAvailable,
    history,
    impersonables,
    loadingImpersonables,
    error: contextError || availableError,
    switchContext, startImpersonation, endImpersonation,
    refresh, refetchAvailable: fetchAvailable, refetchContext: fetchContext,
    clearHistory,
  }), [context, contextError, loadingContext,
       available, availableError, loadingAvailable,
       history, impersonables, loadingImpersonables,
       switchContext, startImpersonation, endImpersonation,
       refresh, fetchAvailable, fetchContext, clearHistory]);

  return <SuiteCtx.Provider value={value}>{children}</SuiteCtx.Provider>;
}

export function useSuiteContext() {
  const v = useContext(SuiteCtx);
  if (!v) {
    return {
      context: null, available: null, history: [], impersonables: [],
      loading: false, loadingContext: false, loadingAvailable: false,
      loadingImpersonables: false,
      contextError: null, availableError: null, error: null,
      switchContext: async () => null,
      startImpersonation: async () => null,
      endImpersonation: async () => null,
      refresh: async () => null,
      refetchAvailable: async () => null,
      refetchContext: async () => null,
      clearHistory: async () => null,
    };
  }
  return v;
}
